import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import Icon from "shared/components/Icon";
import { getChartTypesForReportType } from "../../constants/chartOptions";

/**
 * Shared chart type dropdown. Options depend on reportType (insight, funnel, retention, flow).
 * @param {"insight" | "funnel" | "retention" | "flow"} reportType
 * @param {string} chartType - current chart type id
 * @param {(id: string) => void} onChartTypeChange
 * @param {string[]} [disabledOptionIds] - option ids disabled (e.g. flow topPaths when fewer than two anchors)
 */
export default function ChartTypeDropdown({
  reportType,
  chartType,
  onChartTypeChange,
  funnelMeasuring,
  disabledOptionIds = [],
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const chartTypes = getChartTypesForReportType(reportType || "insight", {
    funnelMeasuring,
  });
  const effectiveChartType =
    reportType === "funnel" &&
    funnelMeasuring === "time_to_convert" &&
    chartType === "funnel-steps"
      ? "bar"
      : chartType;
  const current =
    chartTypes.find((ct) => ct.id === effectiveChartType) || chartTypes[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={buttonRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-8 items-center gap-2 px-3 border rounded-md text-[13px] font-medium transition-colors"
        style={{
          backgroundColor: open ? "var(--surface-sunken)" : "var(--surface-base)",
          borderColor: "var(--border-default)",
          color: "var(--text-primary)",
          fontFamily: "Inter, sans-serif",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "var(--surface-sunken)";
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "var(--surface-base)";
        }}
      >
        {current.iconName ? (
          <Icon name={current.iconName} size={16} color="var(--text-primary)" />
        ) : current.icon ? (
          <current.icon className="w-4 h-4" style={{ color: "var(--text-primary)" }} />
        ) : null}
        <span>{current.label}</span>
        <ChevronDown
          size={16}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "var(--text-secondary)" }}
        />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-1 z-[100] min-w-[200px] border rounded-md shadow-token-lg py-1"
          style={{
            fontFamily: "Inter, sans-serif",
            backgroundColor: "var(--surface-base)",
            borderColor: "var(--border-default)",
          }}
        >
          {chartTypes.map((ct) => {
            const isSelected = effectiveChartType === ct.id;
            const isDisabled = Boolean(ct.disabled) || disabledOptionIds.includes(ct.id);
            return (
              <button
                key={ct.id}
                type="button"
                onClick={() => {
                  if (!isDisabled) {
                    onChartTypeChange(ct.id);
                    setOpen(false);
                  }
                }}
                disabled={isDisabled}
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-[13px] transition-colors"
                style={{
                  backgroundColor: isSelected ? "var(--surface-hover)" : "transparent",
                  color: isSelected ? "var(--text-primary)" : "var(--text-secondary)",
                  opacity: isDisabled ? 0.5 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
              >
                {ct.iconName ? (
                  <Icon
                    name={ct.iconName}
                    size={16}
                    color={isSelected ? "var(--text-primary)" : "var(--text-muted)"}
                  />
                ) : ct.icon ? (
                  <ct.icon
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: isSelected ? "var(--text-primary)" : "var(--text-muted)" }}
                  />
                ) : null}
                <span className="flex-1">{ct.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
